// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EducationalTooltip } from '@/components/educational-tooltip';
import { PageContent } from '@/components/page-content';
import { SegmentedControl } from '@/components/segmented-control';
import {
  DoNotDisturbRounded,
  InfoOutlined,
  RefreshRounded,
  SouthEastRounded,
} from '@/constants/hardfin-standard-icons';
import {
  PROJECT_END_CLASSIFICATIONS,
  PROJECT_END_CLASSIFICATION_LABELS,
  ProjectEndClassification,
  projectEndClassificationSchema,
} from '@/constants/project-end-classification';
import {
  Sort,
  SortDirection,
  invert,
  sortDirectionSchema,
} from '@/constants/sort';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useResizeObserverRef } from '@/hooks/use-resize-observer-ref';
import {
  ProjectSortProperty,
  projectSortPropertySchema,
} from '@/project/api/get-projects';
import { ProjectStatusChip } from '@/project/components/project-status-chip';
import {
  ProjectTable,
  ProjectTableColumn,
} from '@/project/components/project-table';
import { useProjects } from '@/project/hooks/use-projects';
import { ProjectRequestedAssetCountChart } from '@/report/components/project-requested-asset-count-chart';
import { ProjectTotalContractValueChart } from '@/report/components/project-total-contract-value-chart';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import { ReportCard } from '@/report/components/report-card';
import { ReportLocationMap } from '@/report/components/report-location-map';
import { SparseLocation } from '@/sparse/api/types';
import { getToday } from '@/utilities/date-time';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  colors,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { ReactNode, useRef, useState } from 'react';
import { z } from 'zod';

const REPORT_TITLE = 'Upcoming project completions report';
const CHART_HEIGHT = 300;
const PROJECT_END_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
];

const PROJECT_END_CLASSIFICATION_ICONS: Record<
  ProjectEndClassification,
  ReactNode
> = {
  renewed: <RefreshRounded fontSize="small" />,
  contraction: <SouthEastRounded fontSize="small" />,
  churn: <DoNotDisturbRounded fontSize="small" />,
};

const PROJECT_END_CLASSIFICATION_OPTIONS = PROJECT_END_CLASSIFICATIONS.map(
  (classification) => ({
    value: classification,
    label: PROJECT_END_CLASSIFICATION_LABELS[classification],
    icon: PROJECT_END_CLASSIFICATION_ICONS[classification],
  })
);

const TABLE_COLUMNS: ProjectTableColumn[] = [
  'end-date',
  'name',
  'workflow-stage',
  'assets',
  'locations',
  'customer',
];

const searchSchema = z.object({
  days: z.number().optional().catch(undefined),
  sort: projectSortPropertySchema.optional().catch(undefined),
  direction: sortDirectionSchema.optional().catch(undefined),
  classifications: z
    .array(projectEndClassificationSchema)
    .optional()
    .catch(['renewed']),
});

export const Route = createFileRoute(
  '/_app/reports/upcoming-project-completions'
)({
  component: UpcomingProjectCompletionsRoute,
  validateSearch: searchSchema,
});

function UpcomingProjectCompletionsRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const totalContractValueObserver = useResizeObserverRef<HTMLDivElement>();
  const assetCountObserver = useResizeObserverRef<HTMLDivElement>();

  const [focusId, setFocusId] = useState<string | null>(null);
  const [focusColor, setFocusColor] = useState<string | null>(null);

  const classificationsTooltip = useDisclosure();
  const classificationsIconRef = useRef<SVGSVGElement>(null);

  const dayCount = search.days ?? 90;
  const endBy = getToday().plus({ days: dayCount });
  const endClassifications = search.classifications ?? ['renewed'];
  const sort: Sort<ProjectSortProperty> = {
    property: search.sort ?? 'end',
    direction: search.direction ?? 'asc',
  };

  const { data, isPending } = useProjects({
    sort,
    filter: {
      endClassifications,
      statuses: ['active'],
      endBy: endBy.toISODate(),
      pageSize: 10000, // Reports need all matching projects
    },
  });

  const projects = data ?? [];
  const locations = projects.flatMap(({ locations }) => locations);
  const focusedProject = projects.find((project) => project.id === focusId);
  const emptyState = `No projects found ending in ${pluralize(dayCount, { singular: 'day' })} (before ${endBy.plus({ day: 1 }).toLocaleString({ dateStyle: 'medium' })})`;
  const locationsEmptyState =
    projects.length > 0 &&
    projects.every((project) => project.locations.length === 0)
      ? 'No location data for the upcoming projects'
      : emptyState;

  const handleHoverTotalContractValue = (
    id: string | null,
    color: string | null
  ) => {
    setFocusId(id);
    setFocusColor(color);
  };

  const handleHoverAssetCount = (id: string | null, color: string | null) => {
    setFocusId(id);
    setFocusColor(color);
  };

  const handleChangeDayCount = (count: number) => {
    navigate({ search: { ...search, days: count } });
  };

  const handleChangeStartClassifications = (
    classifications: ProjectEndClassification[]
  ) => {
    if (classifications.length > 0) {
      navigate({ search: { ...search, classifications } });
    }
  };

  const setSort = (property: ProjectSortProperty, direction: SortDirection) => {
    navigate({ search: { ...search, direction, sort: property } });
  };

  const handleSort = (property: ProjectSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invert(sort.direction));
    }

    setSort(property, 'asc');
  };

  const getLocationColor = (location: SparseLocation) => {
    const isFocused = focusedProject?.locations.some(
      ({ id }) => id === location.id
    );

    if (isFocused && focusColor !== null) {
      return focusColor;
    }

    return colors.blueGrey[300];
  };

  return (
    <PageContent>
      <ReportBreadcrumbs title={REPORT_TITLE} />
      <Typography variant="h5">{REPORT_TITLE}</Typography>
      <Typography variant="body2" color="text.secondary">
        Includes all projects with an end date in the given range
      </Typography>
      <Stack
        spacing={2}
        marginTop={3}
        direction="row"
        justifyContent="space-between"
      >
        <SegmentedControl
          label="Projects ending"
          value={dayCount}
          onChange={handleChangeDayCount}
          options={PROJECT_END_OPTIONS}
        />
        <Stack spacing={1} direction="row" alignItems="center">
          <EducationalTooltip
            title="Filters for upcoming project completions"
            anchorElement={classificationsIconRef.current}
            isOpen={classificationsTooltip.isOpen}
            onClose={classificationsTooltip.close}
          >
            <Box
              rowGap={2}
              columnGap={1}
              display="grid"
              alignItems="start"
              gridTemplateColumns="min-content 1fr"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <RefreshRounded fontSize="inherit" />
                <Typography variant="subtitle2" whiteSpace="nowrap">
                  Renewed
                </Typography>
              </Stack>
              <Typography variant="body2">
                Another project refers back to this project
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <SouthEastRounded fontSize="inherit" />
                <Typography variant="subtitle2" whiteSpace="nowrap">
                  Contraction
                </Typography>
              </Stack>
              <Typography variant="body2">
                No project refers back to this project, but this customer has an{' '}
                <ProjectStatusChip status="ACTIVE" /> or{' '}
                <ProjectStatusChip status="UPCOMING" /> project
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <DoNotDisturbRounded fontSize="inherit" />
                <Typography variant="subtitle2" whiteSpace="nowrap">
                  Churn
                </Typography>
              </Stack>
              <Typography variant="body2">
                No project refers back to this project, and this customer has no{' '}
                <ProjectStatusChip status="ACTIVE" /> or{' '}
                <ProjectStatusChip status="UPCOMING" /> projects
              </Typography>
            </Box>
          </EducationalTooltip>
          <InfoOutlined
            ref={classificationsIconRef}
            color="action"
            fontSize="small"
            onClick={classificationsTooltip.open}
            sx={{ cursor: 'help' }}
          />
          <SegmentedControl
            multiple
            label="Include"
            value={endClassifications}
            onChange={handleChangeStartClassifications}
            options={PROJECT_END_CLASSIFICATION_OPTIONS}
          />
        </Stack>
      </Stack>
      <Box
        gap={3}
        marginY={3}
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
      >
        <ReportCard ref={totalContractValueObserver.ref} title="Project value">
          {isPending ? (
            <Box
              height={CHART_HEIGHT}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress />
            </Box>
          ) : (
            <ProjectTotalContractValueChart
              groupCount={5}
              width={totalContractValueObserver.width}
              height={CHART_HEIGHT}
              onHover={handleHoverTotalContractValue}
              projects={projects}
              emptyState={emptyState}
            />
          )}
        </ReportCard>
        <ReportCard ref={assetCountObserver.ref} title="Asset count">
          {isPending ? (
            <Box
              height={CHART_HEIGHT}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress />
            </Box>
          ) : (
            <ProjectRequestedAssetCountChart
              groupCount={5}
              width={assetCountObserver.width}
              height={CHART_HEIGHT}
              onHover={handleHoverAssetCount}
              projects={projects}
              emptyState={emptyState}
            />
          )}
        </ReportCard>
        <ReportCard title="Locations">
          <ReportLocationMap
            locations={locations}
            emptyState={locationsEmptyState}
            getColor={getLocationColor}
          />
        </ReportCard>
      </Box>
      <ProjectTable
        sort={sort}
        projects={projects}
        columns={TABLE_COLUMNS}
        onSort={handleSort}
        emptyState={emptyState}
      />
    </PageContent>
  );
}
